import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Player } from '../services/playerService';
import { PlayerFantasyScore } from '../types';
import { nblService } from '../services/nblService';

interface GamePlayerDialogProps {
  open: boolean;
  player: Player | null;
  gameId: number | null;
  gameDate?: Date | null;
  onClose: () => void;
}

const GamePlayerDialog: React.FC<GamePlayerDialogProps> = ({ open, player, gameId, gameDate, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [playerGame, setPlayerGame] = useState<PlayerFantasyScore | null>(null);
  const [gameInfo, setGameInfo] = useState<any>(null);

  useEffect(() => {
    const loadPlayerGameData = async () => {
      if (!open || !player || !gameId) {
        setPlayerGame(null);
        setGameInfo(null);
        return;
      }

      setLoading(true);
      try {
        console.log('GamePlayerDialog: Loading data for player', player.id, 'game', gameId);
        
        // Load all player stats for this game first (this is what we really need)
        const allPlayerStats = await nblService.getPlayerGamesByGameId(gameId);
        console.log('GamePlayerDialog: All player stats loaded', allPlayerStats.length, 'players');
        console.log('GamePlayerDialog: Looking for player ID', player.id, 'Type:', typeof player.id);
        console.log('GamePlayerDialog: Available player IDs:', allPlayerStats.map(p => ({ id: p.playerId, type: typeof p.playerId, name: p.playerName })));
        
        // Find this specific player's stats - try both strict and loose comparison
        let playerStats = allPlayerStats.find(p => p.playerId === player.id);
        
        // If not found with strict comparison, try with type coercion
        if (!playerStats) {
          console.log('GamePlayerDialog: Not found with strict comparison, trying loose');
          playerStats = allPlayerStats.find(p => Number(p.playerId) === Number(player.id));
        }
        
        if (playerStats) {
          console.log('GamePlayerDialog: Found player stats', playerStats);
          setPlayerGame(playerStats);
        } else {
          console.warn('GamePlayerDialog: Player stats not found for player', player.id, 'in game', gameId);
          setPlayerGame(null);
        }
        
        // Try to load game info, but don't fail if it's not available
        try {
          const game = await nblService.getGameById(gameId);
          setGameInfo(game);
          console.log('GamePlayerDialog: Game info loaded', game);
        } catch (gameError) {
          console.warn('GamePlayerDialog: Could not load game info from endpoint, trying games by date', gameError);
          // Fallback: try to get game info from games by date
          if (gameDate) {
            try {
              const dateGames = await nblService.getGamesByDate(gameDate);
              const game = dateGames.find((g: any) => g.id === gameId);
              if (game) {
                setGameInfo(game);
                console.log('GamePlayerDialog: Game info loaded from date games', game);
              } else {
                throw new Error('Game not found in date games');
              }
            } catch (dateError) {
              console.warn('GamePlayerDialog: Could not load game info from date games', dateError);
              // Set minimal game info
              setGameInfo({
                id: gameId,
                homeTeamName: 'Unknown',
                awayTeamName: 'Unknown',
                gameDate: gameDate
              });
            }
          } else {
            // Set minimal game info
            setGameInfo({
              id: gameId,
              homeTeamName: 'Unknown',
              awayTeamName: 'Unknown',
              gameDate: null
            });
          }
        }
      } catch (error) {
        console.error('Error loading player game data:', error);
        setPlayerGame(null);
        setGameInfo(null);
      } finally {
        setLoading(false);
      }
    };

    loadPlayerGameData();
  }, [open, player, gameId]);

  if (!player || !gameId) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={false}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {player.name}
          </Typography>
          {gameInfo && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {gameInfo.awayTeamName} @ {gameInfo.homeTeamName}
              {gameInfo.gameDate && ` • ${new Date(gameInfo.gameDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : playerGame ? (
          <Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Stat</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Minutes Played</TableCell>
                    <TableCell align="right">
                      {Math.floor(playerGame.minutesPlayed / 60)}:{(playerGame.minutesPlayed % 60).toString().padStart(2, '0')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Field Goals</TableCell>
                    <TableCell align="right">{playerGame.fieldGoalsMade}/{playerGame.fieldGoalsAttempted}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3-Pointers</TableCell>
                    <TableCell align="right">{playerGame.threePointersMade}/{playerGame.threePointersAttempted}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Free Throws</TableCell>
                    <TableCell align="right">{playerGame.freeThrowsMade}/{playerGame.freeThrowsAttempted}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Points</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{playerGame.points}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Total Rebounds</TableCell>
                    <TableCell align="right">{playerGame.rebounds}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Offensive Rebounds</TableCell>
                    <TableCell align="right">{playerGame.offensiveRebounds}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Defensive Rebounds</TableCell>
                    <TableCell align="right">{playerGame.defensiveRebounds}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Assists</TableCell>
                    <TableCell align="right">{playerGame.assists}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Steals</TableCell>
                    <TableCell align="right">{playerGame.steals}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Blocks</TableCell>
                    <TableCell align="right">{playerGame.blocks}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Turnovers</TableCell>
                    <TableCell align="right">{playerGame.turnovers}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Fouls</TableCell>
                    <TableCell align="right">{playerGame.fouls}</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'action.selected' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Fantasy Points</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {playerGame.fantasyPoints.toFixed(1)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No game data available for this player on this date.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Player ID: {player.id} | Game ID: {gameId}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GamePlayerDialog;

